import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Send, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, PremiumBadge, SectionTitle, Spinner } from "@/components/common";
import { useLoad, useTrip } from "@/context/TripContext";
import { API, api, errMsg } from "@/lib/api";

const QUICK = ["Draft a day-by-day itinerary that fits our group's interests and budget",
  "Suggest cheaper alternatives to our current plan", "Summarize our polls and traveler preferences",
  "What's missing or conflicting in our plan?", "Draft a friendly announcement about next steps", "Create a packing list for this trip"];
const strip = (t) => t.replace(/```json[\s\S]*?```/g, "").replace(/```json[\s\S]*$/, "").trim();

const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p));
function Md({ text }) {
  return text.split("\n").map((l, i) => {
    const h = l.match(/^#{1,4}\s+(.*)/);
    if (h) return <p key={i} className="mt-3 font-display font-bold first:mt-0">{inline(h[1])}</p>;
    const b = l.match(/^\s*[-*•]\s+(.*)/);
    if (b) return <p key={i} className="flex gap-2 pl-1"><span className="text-orange-500">•</span><span>{inline(b[1])}</span></p>;
    if (/^-{3,}$/.test(l.trim())) return <hr key={i} className="my-2" />;
    return l.trim() ? <p key={i}>{inline(l)}</p> : <div key={i} className="h-2" />;
  });
}

function Msg({ m, onApply }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${mine ? "bg-orange-500 text-white" : "card"}`}>
        {mine ? <p className="whitespace-pre-wrap">{m.content}</p> : <div className="space-y-0.5"><Md text={strip(m.content) || "…"} /></div>}
        {m.has_draft && <Button size="sm" className="mt-3 rounded-full bg-teal-600 hover:bg-teal-700" onClick={() => onApply(m.message_id)} data-testid={`ai-apply-${m.message_id}`}><Sparkles className="mr-1 h-4 w-4" />Add as alternative itinerary</Button>}
      </div>
    </div>
  );
}

async function streamChat(tid, message, onDelta) {
  const send = () => fetch(`${API}/trips/${tid}/ai/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message }) });
  let res = await send();
  if (res.status === 401) { await api.post("/auth/refresh"); res = await send(); }
  if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(typeof j.detail === "string" ? j.detail : "Request failed"); }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "", final = null;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const chunks = buf.split("\n\n");
    buf = chunks.pop();
    for (const c of chunks) {
      if (!c.startsWith("data: ")) continue;
      const ev = JSON.parse(c.slice(6));
      if (ev.delta) onDelta(ev.delta);
      if (ev.error) throw new Error(ev.error);
      if (ev.done) final = ev;
    }
  }
  return final;
}

export default function Concierge() {
  const { tid, limits, reload: reloadTrip } = useTrip();
  const nav = useNavigate();
  const [d, reload] = useLoad(`/trips/${tid}/ai/messages`);
  const [text, setText] = useState("");
  const [live, setLive] = useState(null);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [d, live]);
  if (!limits.features.includes("ai_concierge")) {
    return <EmptyState icon={Bot} title="AI concierge is a Premium feature" text="Get itinerary drafts, cheaper alternatives, poll summaries and packing lists built from your trip's real data. Suggestions are always labelled, and the concierge never books anything."
      action={<Link to="/billing" className="rounded-full bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white" data-testid="ai-upgrade-link">See plans</Link>} testid="ai-locked" />;
  }
  if (!d) return <Spinner />;
  const send = async (msg) => {
    if (!msg.trim() || live !== null) return;
    setText(""); setLive("");
    d.messages.push({ role: "user", content: msg, message_id: `tmp${Date.now()}` });
    try { await streamChat(tid, msg, (t) => setLive((p) => (p || "") + t)); } catch (e) { toast.error(e.message); }
    setLive(null); reload();
  };
  const apply = async (mid) => {
    try { const { data } = await api.post(`/trips/${tid}/ai/apply/${mid}`); toast.success(`Added "${data.name}" with ${data.items} items. Review it, then make it current.`); reloadTrip(); nav(`/trips/${tid}/itinerary`); } catch (e) { toast.error(errMsg(e)); }
  };
  const clear = async () => { await api.delete(`/trips/${tid}/ai/messages`); reload(); };
  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <SectionTitle title="AI concierge" sub="Uses your trip's real data. Prices are estimates, and it never invents bookings."
        right={<div className="flex items-center gap-2"><span className="pill bg-slate-100 text-slate-600" data-testid="ai-usage">{d.usage.used}/{d.usage.cap} this month</span><PremiumBadge plan={d.usage.plan} />
          {d.messages.length > 0 && <Button size="icon" variant="ghost" onClick={clear} aria-label="Clear conversation" data-testid="ai-clear"><Trash2 className="h-4 w-4" /></Button>}</div>} />
      <div className="no-scrollbar flex gap-2 overflow-x-auto">{QUICK.map((q, i) => <button key={q} onClick={() => send(q)} disabled={live !== null} className="shrink-0 rounded-full bg-white px-4 py-2 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-orange-50" data-testid={`ai-quick-${i}`}>{q}</button>)}</div>
      <div className="card grain min-h-[320px] space-y-3 p-4" data-testid="ai-thread">
        {d.messages.length === 0 && live === null && <p className="py-10 text-center text-sm text-slate-500">Ask anything about planning this trip, or tap a suggestion above.</p>}
        {d.messages.map((m) => <Msg key={m.message_id} m={m} onApply={apply} />)}
        {live !== null && <Msg m={{ role: "assistant", content: live || "Thinking…" }} onApply={apply} />}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="flex gap-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(text); } }} placeholder="e.g. Plan 3 days in Lisbon for 8 friends with about €150 per person for activities" data-testid="ai-input" />
        <Button type="submit" className="h-auto rounded-2xl px-5" disabled={live !== null || !text.trim()} aria-label="Send" data-testid="ai-send"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}
